import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { hasPermission } from '../lib/permissions';
import { 
  Flag, 
  Clock, 
  CheckCircle2, 
  Circle,
  AlertTriangle,
  Calendar,
  Edit2,
  Leaf,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function MilestonesPanel({ animalId, animal, user, onUpdate }) {
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForecastDialog, setShowForecastDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Forecast form
  const [forecastDays, setForecastDays] = useState('');
  const [forecastNote, setForecastNote] = useState('');
  const [readyForRelease, setReadyForRelease] = useState(false);

  useEffect(() => {
    if (animalId) {
      fetchMilestones();
    }
  }, [animalId]);

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/api/animals/${animalId}/milestones`);
      setMilestones(response.data);
      
      // Pre-fill form with current values
      if (response.data.treatment_forecast.days) {
        setForecastDays(String(response.data.treatment_forecast.days));
      }
      setForecastNote(response.data.treatment_forecast.note || '');
      setReadyForRelease(response.data.ready_for_release);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForecast = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/animals/${animalId}/forecast`, {
        treatment_forecast_days: forecastDays ? parseInt(forecastDays) : null,
        treatment_forecast_note: forecastNote || null,
        ready_for_release: readyForRelease
      });
      
      toast.success('Previsão atualizada!');
      setShowForecastDialog(false);
      fetchMilestones();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving forecast:', error);
      toast.error('Erro ao salvar previsão');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = hasPermission(user, 'animals.edit_clinical');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  if (!milestones) return null;

  const { days_in_treatment, treatment_forecast, ready_for_release, treatment_ended, death_at_admission } = milestones;

  return (
    <div className="space-y-4" data-testid="milestones-panel">
      {/* Summary Card */}
      <Card className="border-2 border-emerald-200">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-800">
                  {days_in_treatment} dias
                </p>
                <p className="text-sm text-emerald-600">
                  {death_at_admission ? 'sem tratamento' : treatment_ended ? 'tratamento encerrado' : 'em tratamento'}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {treatment_forecast.overdue && (
                <Badge className="bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Previsão ultrapassada
                </Badge>
              )}
              {ready_for_release && (
                <Badge className="bg-green-100 text-green-800 border-green-300 flex items-center gap-1">
                  <Leaf className="w-3 h-3" />
                  Pronto para soltura
                </Badge>
              )}
              {treatment_ended && (
                <Badge className="bg-gray-100 text-gray-700 border-gray-300">
                  Encerrado
                </Badge>
              )}
              {!treatment_forecast.days && (
                <Badge variant="outline" className="text-gray-500">
                  Sem previsão
                </Badge>
              )}
              {treatment_forecast.days && !treatment_forecast.overdue && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                  Previsão: {treatment_forecast.days} dias
                </Badge>
              )}
            </div>
            
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowForecastDialog(true)}
                className="border-emerald-600 text-emerald-600"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Editar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Milestones Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-emerald-800 text-lg">
            <Flag className="w-5 h-5" />
            Marcos de Tratamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            <div className="space-y-4">
              {milestones.milestones.map((milestone, idx) => (
                <div key={idx} className="relative flex items-center gap-4 pl-12">
                  {/* Dot */}
                  <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                    milestone.reached 
                      ? 'bg-emerald-500' 
                      : 'bg-gray-200'
                  }`}>
                    {milestone.reached ? (
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    ) : (
                      <Circle className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 flex items-center justify-between p-2 rounded ${
                    milestone.reached ? 'bg-emerald-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        milestone.reached ? 'text-emerald-700' : 'text-gray-500'
                      }`}>
                        {milestone.label}
                      </span>
                      {milestone.reached && (
                        <Badge variant="outline" className="text-xs text-emerald-600">
                          ✓ Atingido
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {milestone.reached 
                        ? new Date(milestone.reached_date).toLocaleDateString('pt-BR')
                        : `Previsto: ${new Date(milestone.expected_date).toLocaleDateString('pt-BR')}`
                      }
                    </span>
                  </div>
                </div>
              ))}
              
              {/* Forecast milestone */}
              {treatment_forecast.days && (
                <div className="relative flex items-center gap-4 pl-12">
                  <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                    treatment_forecast.reached 
                      ? treatment_forecast.overdue ? 'bg-red-500' : 'bg-green-500'
                      : 'bg-blue-200'
                  }`}>
                    {treatment_forecast.reached ? (
                      treatment_forecast.overdue 
                        ? <AlertTriangle className="w-3 h-3 text-white" />
                        : <Leaf className="w-3 h-3 text-white" />
                    ) : (
                      <TrendingUp className="w-3 h-3 text-blue-600" />
                    )}
                  </div>
                  
                  <div className={`flex-1 flex items-center justify-between p-2 rounded border-2 ${
                    treatment_forecast.overdue 
                      ? 'bg-red-50 border-red-200' 
                      : treatment_forecast.reached 
                        ? 'bg-green-50 border-green-200'
                        : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        treatment_forecast.overdue ? 'text-red-700' : 'text-blue-700'
                      }`}>
                        Previsão de alta ({treatment_forecast.days} dias)
                      </span>
                      {treatment_forecast.overdue && (
                        <Badge className="bg-red-100 text-red-800 text-xs">
                          {days_in_treatment - treatment_forecast.days} dias atrasado
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(treatment_forecast.expected_date).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {treatment_forecast.note && (
            <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Nota:</strong> {treatment_forecast.note}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Forecast Dialog */}
      <Dialog open={showForecastDialog} onOpenChange={setShowForecastDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Previsão de Tratamento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Tempo Estimado de Tratamento</Label>
              <Select value={forecastDays} onValueChange={setForecastDays}>
                <SelectTrigger data-testid="forecast-days-select">
                  <SelectValue placeholder="Selecione ou deixe vazio para 'Sem previsão'" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem previsão</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="15">15 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                  <SelectItem value="120">120 dias</SelectItem>
                  <SelectItem value="180">180 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Observações sobre a previsão</Label>
              <Input
                value={forecastNote}
                onChange={(e) => setForecastNote(e.target.value)}
                placeholder="Ex: Dependendo da recuperação da asa"
                data-testid="forecast-note-input"
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded border border-green-200">
              <input
                type="checkbox"
                id="ready-release"
                checked={readyForRelease}
                onChange={(e) => setReadyForRelease(e.target.checked)}
                className="w-5 h-5"
              />
              <div>
                <Label htmlFor="ready-release" className="font-medium text-green-800 cursor-pointer">
                  Pronto para soltura
                </Label>
                <p className="text-xs text-green-600">
                  Marcar quando o animal estiver apto para ser solto
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForecastDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveForecast} 
              disabled={saving}
              className="bg-emerald-600"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
